/**
 * TelemetrySdk — Client-side telemetry collection with batching, buffering & persistence.
 *
 * Provides:
 *  - Event tracking (page views, feature usage, actions)
 *  - Automatic heartbeats with sync/system info
 *  - Intelligent batching with configurable flush intervals
 *  - Offline buffering with localStorage persistence
 *  - Session management
 *
 * Usage:
 *   const sdk = new TelemetrySdk({ apiBaseUrl, tenantId, userId, platform });
 *   sdk.start();
 *   sdk.trackPageView('/dashboard');
 *   sdk.trackFeature('POS_BILLING', 'invoice_created', { amount: 500 });
 *   sdk.stop();
 */

// ── Types ──────────────────────────────────────────────────

export interface TelemetryConfig {
  /** Base URL of the API server (e.g. http://localhost:5001) */
  apiBaseUrl: string;
  /** Tenant UUID */
  tenantId: string;
  /** Current user UUID */
  userId: string;
  /** Access token used for tenant-scoped telemetry writes */
  authToken?: string;
  /** Resolve the latest access token after refresh or session hydration. */
  getAuthToken?: () => string | null | undefined;
  /** Optional store UUID */
  storeId?: string;
  /** Unique device identifier (fingerprint or generated) */
  deviceId: string;
  /** Platform identifier */
  platform: "web" | "desktop" | "mobile";
  /** App version string */
  appVersion?: string;
  /** Max events to buffer before auto-flush (default: 20) */
  batchSize?: number;
  /** Flush interval in ms (default: 30000 = 30s) */
  flushIntervalMs?: number;
  /** Heartbeat interval in ms (default: 60000 = 60s) */
  heartbeatIntervalMs?: number;
  /** Max events to store offline (default: 500) */
  maxBufferSize?: number;
  /** localStorage key prefix (default: 'mcp_telemetry') */
  storagePrefix?: string;
  /** Optional callback to collect system info for heartbeats */
  getSystemInfo?: () => Promise<SystemInfo> | SystemInfo;
  /** Optional callback to collect sync status for heartbeats */
  getSyncStatus?: () => Promise<SyncStatus> | SyncStatus;
  /** Enable debug logging (default: false) */
  debug?: boolean;
}

export interface TelemetryEvent {
  deviceId?: string;
  platform: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, unknown>;
  sessionId?: string;
  appVersion?: string;
  timestamp: string;
}

export interface SystemInfo {
  os?: string;
  browser?: string;
  screenResolution?: string;
  memoryMB?: number;
  cpuCores?: number;
  batteryLevel?: number;
  networkType?: string;
}

export interface SyncStatus {
  pendingChanges: number;
  lastSyncAt?: string;
  syncQueueDepth: number;
}

export interface HeartbeatPayload {
  deviceId: string;
  platform: string;
  appVersion?: string;
  systemInfo?: SystemInfo;
  syncStatus?: SyncStatus;
  appState?: {
    currentPage: string;
    activeModule?: string;
    openSince: string;
  };
}

// ── SDK ────────────────────────────────────────────────────

export class TelemetrySdk {
  private config: Required<
    Pick<
      TelemetryConfig,
      | "batchSize"
      | "flushIntervalMs"
      | "heartbeatIntervalMs"
      | "maxBufferSize"
      | "storagePrefix"
      | "debug"
    >
  > &
    TelemetryConfig;

  private buffer: TelemetryEvent[] = [];
  private sessionId: string;
  private sessionStartedAt: string;
  private currentPage = "/";
  private activeModule: string | undefined;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private isRunning = false;
  private isFlushing = false;
  private rejectedAuthToken: string | null = null;

  private currentAuthToken(): string | undefined {
    const token = this.config.getAuthToken?.() || this.config.authToken;
    if (!token || token === this.rejectedAuthToken) return undefined;
    return token;
  }

  private requestHeaders(idempotencyKey: string, authToken: string) {
    return {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
      Authorization: `Bearer ${authToken}`,
      "X-Client-App": this.config.platform,
      "X-Client-Version": this.config.appVersion ?? "1.0.0",
    };
  }

  constructor(config: TelemetryConfig) {
    // Strip trailing /api or /api/v1 from base URL — the SDK appends /api/v1/... itself
    const cleanedBaseUrl = config.apiBaseUrl
      .replace(/\/+$/, "")
      .replace(/\/api(\/v1)?$/, "");

    this.config = {
      batchSize: 20,
      flushIntervalMs: 30_000,
      heartbeatIntervalMs: 60_000,
      maxBufferSize: 500,
      storagePrefix: "mcp_telemetry",
      debug: false,
      ...config,
      apiBaseUrl: cleanedBaseUrl,
    };

    this.sessionId = this.generateSessionId();
    this.sessionStartedAt = new Date().toISOString();

    // Restore any events from previous offline buffer
    this.restoreBuffer();
  }

  // ── Lifecycle ──────────────────────────────────────────

  /**
   * Start the telemetry SDK — begins flush timer and heartbeat timer.
   */
  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.log("SDK started");

    // Track session start
    this.track("session", "start", { label: "app_open" });

    // Send initial heartbeat immediately
    this.sendHeartbeat().catch(() => {});

    // Schedule periodic flush
    this.flushTimer = setInterval(() => {
      this.flush().catch(() => {});
    }, this.config.flushIntervalMs);

    // Schedule periodic heartbeat
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat().catch(() => {});
    }, this.config.heartbeatIntervalMs);

    // Flush on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", this.handleUnload);
      document.addEventListener("visibilitychange", this.handleVisibility);
    }
  }

  /**
   * Stop the SDK — flushes remaining events and stops timers.
   */
  async stop(): Promise<void> {
    if (!this.isRunning) return;
    this.isRunning = false;

    this.track("session", "end", { label: "app_close" });

    if (this.flushTimer) clearInterval(this.flushTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.flushTimer = null;
    this.heartbeatTimer = null;

    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", this.handleUnload);
      document.removeEventListener("visibilitychange", this.handleVisibility);
    }

    await this.flush();
    this.log("SDK stopped");
  }

  /**
   * Update the user/tenant context (e.g. after login or store switch)
   */
  updateContext(
    partial: Partial<Pick<TelemetryConfig, "userId" | "storeId" | "tenantId">>,
  ): void {
    if (partial.userId) this.config.userId = partial.userId;
    if (partial.storeId) this.config.storeId = partial.storeId;
    if (partial.tenantId) this.config.tenantId = partial.tenantId;
  }

  // ── Tracking Methods ───────────────────────────────────

  /**
   * Track a generic event
   */
  track(
    category: string,
    action: string,
    options?: {
      label?: string;
      value?: number;
      metadata?: Record<string, unknown>;
    },
  ): void {
    const event: TelemetryEvent = {
      deviceId: this.config.deviceId,
      platform: this.config.platform,
      category,
      action,
      label: options?.label,
      value: options?.value,
      metadata: options?.metadata,
      sessionId: this.sessionId,
      appVersion: this.config.appVersion,
      timestamp: new Date().toISOString(),
    };

    this.addToBuffer(event);
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, metadata?: Record<string, unknown>): void {
    this.currentPage = path;

    // Infer module from path
    this.activeModule = this.inferModule(path);

    this.track("navigation", "page_view", {
      label: path,
      metadata: { ...metadata, referrer: this.currentPage },
    });
  }

  /**
   * Track feature usage (maps to a module)
   */
  trackFeature(
    moduleCode: string,
    action: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.track("feature", action, {
      label: moduleCode,
      metadata,
    });
  }

  /**
   * Track a user action (button click, form submit, etc.)
   */
  trackAction(
    action: string,
    label?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.track("action", action, { label, metadata });
  }

  /**
   * Track an error
   */
  trackError(error: Error | string, context?: Record<string, unknown>): void {
    const errorObj = typeof error === "string" ? new Error(error) : error;
    this.track("error", "client_error", {
      label: errorObj.message,
      metadata: {
        stack: errorObj.stack?.slice(0, 1000),
        page: this.currentPage,
        ...context,
      },
    });
  }

  /**
   * Track a search event
   */
  trackSearch(query: string, resultCount: number, context?: string): void {
    this.track("search", "query", {
      label: context || this.currentPage,
      value: resultCount,
      metadata: { query: query.slice(0, 200) },
    });
  }

  // ── Internal: Buffer Management ────────────────────────

  private addToBuffer(event: TelemetryEvent): void {
    this.buffer.push(event);

    // Enforce max buffer size (drop oldest)
    if (this.buffer.length > this.config.maxBufferSize) {
      this.buffer = this.buffer.slice(-this.config.maxBufferSize);
    }

    // Auto-flush when batch size reached
    if (this.buffer.length >= this.config.batchSize) {
      this.flush().catch(() => {});
    }

    // Persist buffer for offline resilience
    this.persistBuffer();
  }

  /**
   * Flush buffered events to the server
   */
  async flush(): Promise<void> {
    const authToken = this.currentAuthToken();
    if (!authToken) {
      this.log("flush() skipped — no valid auth token");
      return;
    }

    // Guard: don't send if tenantId is not a valid UUID to avoid persistent 400s
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        this.config.tenantId,
      )
    ) {
      this.log("flush() skipped — tenantId is not a valid UUID");
      return;
    }
    if (this.isFlushing || this.buffer.length === 0) return;
    this.isFlushing = true;

    const eventsToSend = this.buffer.map((event) =>
      this.withoutScopeFields(event),
    );
    this.buffer = [];
    this.persistBuffer();

    try {
      const resp = await fetch(
        `${this.config.apiBaseUrl}/api/v1/telemetry/events`,
        {
          method: "POST",
          headers: this.requestHeaders(crypto.randomUUID(), authToken),
          body: JSON.stringify({ events: eventsToSend }),
          keepalive: true, // Survives page unload
        },
      );

      if (!resp.ok) {
        if (resp.status === 401) this.rejectedAuthToken = authToken;
        throw new Error(`HTTP ${resp.status}`);
      }

      this.log(`Flushed ${eventsToSend.length} events`);
    } catch (err) {
      // Put events back in buffer for retry
      this.buffer = [...eventsToSend, ...this.buffer].slice(
        -this.config.maxBufferSize,
      );
      this.persistBuffer();
      this.log(`Flush failed, ${this.buffer.length} events buffered`, err);
    } finally {
      this.isFlushing = false;
    }
  }

  // ── Internal: Heartbeat ────────────────────────────────

  private async sendHeartbeat(): Promise<void> {
    const authToken = this.currentAuthToken();
    if (!authToken) {
      this.log("sendHeartbeat() skipped — no valid auth token");
      return;
    }

    // Guard: don't send if tenantId is not a valid UUID
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        this.config.tenantId,
      )
    ) {
      this.log("sendHeartbeat() skipped — tenantId is not a valid UUID");
      return;
    }
    try {
      const systemInfo = this.config.getSystemInfo
        ? await this.config.getSystemInfo()
        : this.getDefaultSystemInfo();

      const syncStatus = this.config.getSyncStatus
        ? await this.config.getSyncStatus()
        : { pendingChanges: 0, syncQueueDepth: 0 };

      const payload: HeartbeatPayload = {
        deviceId: this.config.deviceId,
        platform: this.config.platform,
        appVersion: this.config.appVersion,
        systemInfo,
        syncStatus,
        appState: {
          currentPage: this.currentPage,
          activeModule: this.activeModule,
          openSince: this.sessionStartedAt,
        },
      };

      const resp = await fetch(
        `${this.config.apiBaseUrl}/api/v1/telemetry/heartbeat`,
        {
          method: "POST",
          headers: this.requestHeaders(crypto.randomUUID(), authToken),
          body: JSON.stringify(payload),
        },
      );

      if (!resp.ok) {
        if (resp.status === 401) this.rejectedAuthToken = authToken;
        throw new Error(`HTTP ${resp.status}`);
      }

      this.log("Heartbeat sent");
    } catch (err) {
      this.log("Heartbeat failed", err);
    }
  }

  // ── Internal: Persistence ──────────────────────────────

  private persistBuffer(): void {
    try {
      if (typeof localStorage === "undefined") return;
      const key = `${this.config.storagePrefix}_buffer`;
      if (this.buffer.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(this.buffer));
      }
    } catch {
      // localStorage might be full or unavailable
    }
  }

  private restoreBuffer(): void {
    try {
      if (typeof localStorage === "undefined") return;
      const key = `${this.config.storagePrefix}_buffer`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const events = JSON.parse(stored) as TelemetryEvent[];
        // Scope is derived from the authenticated request. Remove fields from
        // legacy persisted buffers too, otherwise an older client payload can
        // be rejected forever by the API's forbidNonWhitelisted validation.
        const sanitized = events
          .slice(-this.config.maxBufferSize)
          .map((event) => this.withoutScopeFields(event));
        this.buffer = sanitized;
        this.log(`Restored ${this.buffer.length} buffered events`);
      }
    } catch {
      // Corrupted data, start fresh
      this.buffer = [];
    }
  }

  // ── Internal: Event Handlers ───────────────────────────

  private handleUnload = (): void => {
    // Keep the auth and idempotency headers on unload delivery. sendBeacon
    // cannot set either header, so use keepalive fetch instead.
    const authToken = this.currentAuthToken();
    if (this.buffer.length > 0 && authToken) {
      const payload = JSON.stringify({
        events: this.buffer.map((event) => this.withoutScopeFields(event)),
      });
      void fetch(`${this.config.apiBaseUrl}/api/v1/telemetry/events`, {
        method: "POST",
        headers: this.requestHeaders(crypto.randomUUID(), authToken),
        body: payload,
        keepalive: true,
      });
      this.buffer = [];
      this.persistBuffer();
    }
  };

  private withoutScopeFields(event: TelemetryEvent): TelemetryEvent {
    const sanitized = { ...event } as TelemetryEvent & Record<string, unknown>;
    delete sanitized.tenantId;
    delete sanitized.userId;
    delete sanitized.storeId;
    return sanitized;
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === "hidden") {
      this.flush().catch(() => {});
    }
  };

  // ── Internal: Helpers ──────────────────────────────────

  private getDefaultSystemInfo(): SystemInfo {
    if (typeof window === "undefined") return {};
    const nav = window.navigator as any;
    return {
      os: nav.platform || nav.userAgentData?.platform || undefined,
      browser: nav.userAgent?.slice(0, 200),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      memoryMB: nav.deviceMemory ? nav.deviceMemory * 1024 : undefined,
      cpuCores: nav.hardwareConcurrency || undefined,
      networkType: nav.connection?.effectiveType || undefined,
    };
  }

  private inferModule(path: string): string | undefined {
    const moduleMap: Record<string, string> = {
      "/billing": "POS_BILLING",
      "/invoice": "POS_BILLING",
      "/pos": "POS_BILLING",
      "/inventory": "INVENTORY",
      "/products": "INVENTORY",
      "/batches": "INVENTORY",
      "/customers": "CUSTOMER_MGMT",
      "/reports": "REPORTS_BASIC",
      "/compliance": "COMPLIANCE",
      "/purchase": "PROCUREMENT",
      "/suppliers": "PROCUREMENT",
    };

    for (const [prefix, module] of Object.entries(moduleMap)) {
      if (path.startsWith(prefix)) return module;
    }
    return undefined;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  private log(msg: string, ...args: unknown[]): void {
    if (this.config.debug) {
      console.log(`[TelemetrySdk] ${msg}`, ...args);
    }
  }
}

// ── React Hook Helpers ───────────────────────────────────

let _sdkInstance: TelemetrySdk | null = null;

/** UUID v4 format check */
function isValidUUID(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

/**
 * Initialize the global TelemetrySdk instance.
 * Call once at app startup after auth is established.
 * Returns null and logs a warning if tenantId or userId are not valid UUIDs.
 */
export function initTelemetry(config: TelemetryConfig): TelemetrySdk | null {
  if (!isValidUUID(config.tenantId)) {
    console.warn(
      `[TelemetrySdk] Skipping init — tenantId is not a valid UUID: "${
        config.tenantId
      }". Telemetry will not be collected until a valid tenantId is provided.`,
    );
    return null;
  }
  if (!isValidUUID(config.userId)) {
    console.warn(
      `[TelemetrySdk] Skipping init — userId is not a valid UUID: "${
        config.userId
      }".`,
    );
    return null;
  }
  if (_sdkInstance) {
    _sdkInstance.stop().catch(() => {});
  }
  _sdkInstance = new TelemetrySdk(config);
  _sdkInstance.start();
  return _sdkInstance;
}

/**
 * Get the current TelemetrySdk instance, or null if not initialized.
 */
export function getTelemetry(): TelemetrySdk | null {
  return _sdkInstance;
}

/**
 * Stop and destroy the current TelemetrySdk instance.
 */
export async function destroyTelemetry(): Promise<void> {
  if (_sdkInstance) {
    await _sdkInstance.stop();
    _sdkInstance = null;
  }
}

/**
 * Generate a stable device ID (persisted in localStorage).
 */
export function getOrCreateDeviceId(prefix = "mcp"): string {
  const key = `${prefix}_device_id`;
  if (typeof localStorage !== "undefined") {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(key, id);
    return id;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
